import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import {
  Target,
  Plus,
  Eye,
  Edit,
  Trash2,
  Phone,
  Mail,
  Globe,
  Users,
  MessageSquare,
  FileText,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Search,
  Grid,
  List,
  ArrowRight,
} from 'lucide-react';

const Leads = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [options, setOptions] = useState({ sources: [], types: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: '',
  });

  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    source: 'Telephone Query',
    lead_type: 'Request for Quote',
    job_type: 'private',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    location: '',
    address: '',
    action_date: '',
    expiry_date: '',
    assigned_to: '',
    notes: '',
    // Insurance fields
    insurer_name: '',
    insurer_contact_person: '',
    insurer_email: '',
    insurer_contact_no: '',
    insurer_reference_no: '',
    insurer_claim_no: '',
    loss_adjuster_name: '',
    loss_adjuster_contact: '',
    loss_adjuster_contact_no: '',
    loss_adjuster_email: '',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leadsRes, membersRes, optionsRes] = await Promise.all([
        leadsAPI.getAll(),
        teamMembersAPI.getAll(),
        leadsAPI.getOptions(),
      ]);
      setLeads(leadsRes.data);
      setTeamMembers(membersRes.data.filter(m => m.status === 'active'));
      setOptions(optionsRes.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async () => {
    if (!createForm.title || !createForm.contact_name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await leadsAPI.create(createForm);
      toast.success('Lead created successfully');
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        source: 'Telephone Query',
        lead_type: 'Request for Quote',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        company_name: '',
        location: '',
        address: '',
        action_date: '',
        expiry_date: '',
        assigned_to: '',
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.source && lead.source !== filters.source) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        lead.title?.toLowerCase().includes(search) ||
        lead.lead_code?.toLowerCase().includes(search) ||
        lead.contact_name?.toLowerCase().includes(search) ||
        lead.company_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const statusConfig = {
    new: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, label: 'New' },
    assigned: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Users, label: 'Assigned' },
    started: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: FileText, label: 'Started' },
    closed_won: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Closed Won' },
    closed_lost: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Closed Lost' },
  };

  const sourceIcons = {
    'Telephone Query': Phone,
    'Email Query': Mail,
    'Word of Mouth': MessageSquare,
    'Walk-in': Users,
    'Website': Globe,
  };

  const getSourceIcon = (source) => {
    const Icon = sourceIcons[source] || FileText;
    return Icon;
  };

  const isExpiringSoon = (lead) => {
    if (!lead.expiry_date) return false;
    const expiry = parseISO(lead.expiry_date);
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    return isBefore(expiry, threeDaysFromNow) && isAfter(expiry, now);
  };

  const isExpired = (lead) => {
    if (!lead.expiry_date) return false;
    return isBefore(parseISO(lead.expiry_date), new Date());
  };

  // Stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    assigned: leads.filter(l => l.status === 'assigned').length,
    started: leads.filter(l => l.status === 'started').length,
    won: leads.filter(l => l.status === 'closed_won').length,
    lost: leads.filter(l => l.status === 'closed_lost').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lead Management
          </h1>
          <p className="text-slate-600">Track and manage sales leads and inquiries</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Lead
          </button>
        )}
      </div>

      {/* Stats Cards - Clickable for 1st Tier Filtering */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <button
          onClick={() => setFilters({ ...filters, status: '' })}
          className={`bg-white border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === '' ? 'border-slate-400 ring-2 ring-slate-200' : 'border-slate-200'
          }`}
        >
          <p className="text-sm text-slate-500">Total Leads</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </button>
        <button
          onClick={() => setFilters({ ...filters, status: 'new' })}
          className={`bg-blue-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === 'new' ? 'border-blue-400 ring-2 ring-blue-200' : 'border-blue-200'
          }`}
        >
          <p className="text-sm text-blue-600">New</p>
          <p className="text-2xl font-bold text-blue-700">{stats.new}</p>
        </button>
        <button
          onClick={() => setFilters({ ...filters, status: 'assigned' })}
          className={`bg-purple-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === 'assigned' ? 'border-purple-400 ring-2 ring-purple-200' : 'border-purple-200'
          }`}
        >
          <p className="text-sm text-purple-600">Assigned</p>
          <p className="text-2xl font-bold text-purple-700">{stats.assigned}</p>
        </button>
        <button
          onClick={() => setFilters({ ...filters, status: 'started' })}
          className={`bg-yellow-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === 'started' ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-yellow-200'
          }`}
        >
          <p className="text-sm text-yellow-600">Started</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.started}</p>
        </button>
        <button
          onClick={() => setFilters({ ...filters, status: 'closed_won' })}
          className={`bg-green-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === 'closed_won' ? 'border-green-400 ring-2 ring-green-200' : 'border-green-200'
          }`}
        >
          <p className="text-sm text-green-600">Won</p>
          <p className="text-2xl font-bold text-green-700">{stats.won}</p>
        </button>
        <button
          onClick={() => setFilters({ ...filters, status: 'closed_lost' })}
          className={`bg-red-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            filters.status === 'closed_lost' ? 'border-red-400 ring-2 ring-red-200' : 'border-red-200'
          }`}
        >
          <p className="text-sm text-red-600">Lost</p>
          <p className="text-2xl font-bold text-red-700">{stats.lost}</p>
        </button>
      </div>

      {/* 2nd Tier Filters - Detailed Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters:</span>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            {options.statuses.map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">All Sources</option>
            {options.sources.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-slate-100' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-slate-100' : ''}`}
            >
              <Grid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Leads List/Grid */}
      {filteredLeads.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No leads found</h3>
          <p className="text-slate-600 mb-4">Create your first lead to get started.</p>
          {canEdit && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Create Lead
            </button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Lead</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Contact</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Source</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Assigned To</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Dates</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredLeads.map((lead) => {
                const config = statusConfig[lead.status] || statusConfig.new;
                const StatusIcon = config.icon;
                const SourceIcon = getSourceIcon(lead.source);
                const expired = isExpired(lead);
                const expiringSoon = isExpiringSoon(lead);

                return (
                  <tr key={lead.id} className={`hover:bg-slate-50 ${expired ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-xs font-mono font-bold text-blue-600 mr-2">
                          {lead.lead_code}
                        </span>
                        <span className="font-medium text-slate-900">{lead.title}</span>
                        {lead.converted_to_job && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            Converted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium text-slate-900">{lead.contact_name}</p>
                        {lead.company_name && <p className="text-slate-500">{lead.company_name}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm text-slate-600">
                        <SourceIcon className="w-3.5 h-3.5" />
                        {lead.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {lead.lead_type}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {lead.assigned_to_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {config.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">
                        <p className="text-slate-500">
                          Captured: {format(parseISO(lead.capture_date), 'MMM dd')}
                        </p>
                        {lead.expiry_date && (
                          <p className={`${expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-orange-600' : 'text-slate-500'}`}>
                            {expired ? '⚠ Expired' : expiringSoon ? '⚠ Expiring soon' : ''} {format(parseISO(lead.expiry_date), 'MMM dd')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          className="p-1.5 hover:bg-slate-100 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        {canEdit && !lead.converted_to_job && (
                          <button
                            onClick={() => handleDeleteLead(lead.id)}
                            className="p-1.5 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLeads.map((lead) => {
            const config = statusConfig[lead.status] || statusConfig.new;
            const StatusIcon = config.icon;
            const SourceIcon = getSourceIcon(lead.source);
            const expired = isExpired(lead);

            return (
              <div
                key={lead.id}
                onClick={() => navigate(`/leads/${lead.id}`)}
                className={`bg-white border rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow ${
                  expired ? 'border-red-300 bg-red-50' : 'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-mono font-bold text-blue-600">
                    {lead.lead_code}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {config.label}
                  </span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{lead.title}</h3>
                <div className="text-sm text-slate-600 mb-3">
                  <p className="font-medium">{lead.contact_name}</p>
                  {lead.company_name && <p>{lead.company_name}</p>}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <SourceIcon className="w-3.5 h-3.5" />
                    {lead.source}
                  </span>
                  <span>{format(parseISO(lead.capture_date), 'MMM dd, yyyy')}</span>
                </div>
                {lead.converted_to_job && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <ArrowRight className="w-3 h-3" />
                      Converted to Job
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Lead Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Create New Lead</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="e.g., Office Renovation Quote Request"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Source *</label>
                  <select
                    value={createForm.source}
                    onChange={(e) => setCreateForm({ ...createForm, source: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {options.sources.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Type *</label>
                  <select
                    value={createForm.lead_type}
                    onChange={(e) => setCreateForm({ ...createForm, lead_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    {options.types.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Type *</label>
                <select
                  value={createForm.job_type}
                  onChange={(e) => setCreateForm({ ...createForm, job_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="private">Private</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>

              {createForm.job_type === 'insurance' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                  <h3 className="font-medium text-purple-900 mb-2">Insurance Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Insurer Name</label>
                      <input type="text" value={createForm.insurer_name} onChange={(e) => setCreateForm({ ...createForm, insurer_name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Insurer Contact</label>
                      <input type="text" value={createForm.insurer_contact_person} onChange={(e) => setCreateForm({ ...createForm, insurer_contact_person: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Insurer Email</label>
                      <input type="email" value={createForm.insurer_email} onChange={(e) => setCreateForm({ ...createForm, insurer_email: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Insurer Phone</label>
                      <input type="tel" value={createForm.insurer_contact_no} onChange={(e) => setCreateForm({ ...createForm, insurer_contact_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Reference No</label>
                      <input type="text" value={createForm.insurer_reference_no} onChange={(e) => setCreateForm({ ...createForm, insurer_reference_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Claim No</label>
                      <input type="text" value={createForm.insurer_claim_no} onChange={(e) => setCreateForm({ ...createForm, insurer_claim_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Loss Adjuster Name</label>
                      <input type="text" value={createForm.loss_adjuster_name} onChange={(e) => setCreateForm({ ...createForm, loss_adjuster_name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Loss Adjuster Contact</label>
                      <input type="text" value={createForm.loss_adjuster_contact} onChange={(e) => setCreateForm({ ...createForm, loss_adjuster_contact: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Loss Adjuster Phone</label>
                      <input type="tel" value={createForm.loss_adjuster_contact_no} onChange={(e) => setCreateForm({ ...createForm, loss_adjuster_contact_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Loss Adjuster Email</label>
                      <input type="email" value={createForm.loss_adjuster_email} onChange={(e) => setCreateForm({ ...createForm, loss_adjuster_email: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Name *</label>
                  <input
                    type="text"
                    value={createForm.contact_name}
                    onChange={(e) => setCreateForm({ ...createForm, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={createForm.company_name}
                    onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={createForm.contact_email}
                    onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={createForm.contact_phone}
                    onChange={(e) => setCreateForm({ ...createForm, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                <input
                  type="text"
                  value={createForm.location}
                  onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Site/Project location"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Action Date</label>
                  <input
                    type="date"
                    value={createForm.action_date}
                    onChange={(e) => setCreateForm({ ...createForm, action_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Expiry Date</label>
                  <input
                    type="date"
                    value={createForm.expiry_date}
                    onChange={(e) => setCreateForm({ ...createForm, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign To</label>
                <select
                  value={createForm.assigned_to}
                  onChange={(e) => setCreateForm({ ...createForm, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Details about the lead..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLead}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
